import {
    createStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Typography,
    useMediaQuery,
    useTheme,
    WithStyles,
    withStyles
} from "@material-ui/core";
import { useGetManySavedSearchSendsQuery } from "../../../../api/auctionApiSlice";
import { formatAuctionDateTime } from "../../../../helpers/auctionDateTime";
import {
    formatLocationQueryString,
    formatPhoneNumberFromE164
} from "../../../../helpers/format";
import ISavedSearchSend from "../../../../models/savedSearchSend/ISavedSearchSend";
import TableFooter from "../../../shared/TableFooter";

interface IAuctionSavedSearchSendTab extends WithStyles<typeof styles> {
    auctionId: string;
}

const styles = (theme: Theme) =>
    createStyles({
        table: {},
        tableCell: {
            borderColor: theme.palette.divider
        },
        tableRow: {
            "&:nth-of-type(odd)": {
                backgroundColor: theme.palette.background.default
            },
            borderColor: theme.palette.grey[500]
        },
        tableCellHeader: { borderColor: "transparent" },
        tableHeader: { fontWeight: "bold" },
        formSection: {
            marginBottom: theme.spacing(5),
            backgroundColor: theme.palette.common.black,
            padding: "32px"
        }
    });

interface Column<T> {
    name: string;
    hideOnMobile?: boolean;
    value: (row: T) => string;
}

const COLS: Column<ISavedSearchSend>[] = [
    { name: "Name", value: (row) => `${row.firstName} ${row.lastName}` },
    {
        name: "Phone Number",
        value: (row) => formatPhoneNumberFromE164(row.phoneNumber)
    },
    { name: "Email", value: (row) => row.email, hideOnMobile: true },
    {
        name: "Date Signed Up",
        value: (row) =>
            formatAuctionDateTime(row.createDate)?.mediumDateTime ?? "",
        hideOnMobile: true
    },
    {
        name: "Query String",
        value: (row) => formatLocationQueryString(row.locationQueryString),
        hideOnMobile: true
    },
    {
        name: "Radius",
        value: (row) => `${row.radiusKm} KM`,
        hideOnMobile: true
    },
    {
        name: "Date Sent",
        value: (row) =>
            row.sentDate
                ? (formatAuctionDateTime(row.sentDate)?.mediumDateTime ?? "")
                : "Not Sent"
    }
];

const NUM_DESKTOP_COLS = COLS.length;
const NUM_MOBILE_COLS = COLS.filter((col) => !col.hideOnMobile).length;
const POLLING_INTERVAL = 60 * 1000; // 1 minute

function AuctionSavedSearchSendTab({
    auctionId,
    classes
}: IAuctionSavedSearchSendTab) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const query = useGetManySavedSearchSendsQuery(auctionId, {
        pollingInterval: POLLING_INTERVAL,
        refetchOnFocus: true,
        skipPollingIfUnfocused: true
    });

    if (query.error) {
        return (
            <div className={classes.formSection}>
                <Typography variant="body2" color="error">
                    An error occurred while fetching saved search sends.
                </Typography>
            </div>
        );
    }

    return (
        <>
            <div className={classes.formSection} />
            <div className={classes.formSection}>
                <Table className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                className={classes.tableCellHeader}
                                colSpan={
                                    isMobile
                                        ? NUM_MOBILE_COLS
                                        : NUM_DESKTOP_COLS
                                }
                            >
                                <Typography
                                    className={classes.tableHeader}
                                    variant="subtitle1"
                                >
                                    Saved Search Sends
                                </Typography>
                            </TableCell>
                        </TableRow>

                        <TableRow>
                            {COLS.map((col) => {
                                if (isMobile && col.hideOnMobile) return null;
                                return (
                                    <TableCell
                                        key={col.name}
                                        className={classes.tableCell}
                                        size="small"
                                    >
                                        {col.name}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {query.data?.length ? (
                            query.data?.map((savedSearchSend) => (
                                <TableRow
                                    key={savedSearchSend.savedSearchSendId}
                                    className={classes.tableRow}
                                >
                                    {COLS.map((col) => {
                                        if (isMobile && col.hideOnMobile)
                                            return null;
                                        return (
                                            <TableCell
                                                key={col.name}
                                                className={classes.tableCell}
                                                size="small"
                                            >
                                                {col.value(savedSearchSend)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    className={classes.tableCell}
                                    size="small"
                                    colSpan={
                                        isMobile
                                            ? NUM_MOBILE_COLS
                                            : NUM_DESKTOP_COLS
                                    }
                                >
                                    <Typography
                                        component="div"
                                        variant="body2"
                                        align="center"
                                        color="textSecondary"
                                    >
                                        No saved search sends for this sale.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter
                        colSpan={isMobile ? NUM_MOBILE_COLS : NUM_DESKTOP_COLS}
                        count={query.data?.length ?? 0}
                        helperText="SMS notifications sent to users with a saved search matching this sale's location."
                    />
                </Table>
            </div>
        </>
    );
}

export default withStyles(styles)(AuctionSavedSearchSendTab);
